import { useEffect, useRef, useState } from "react";
import { InlineWidget } from "react-calendly";
import { ScheduleLoader } from "@/pages/demo/scheduleLoader";
import { analyticsService } from "services/analyticsService";
import { demoFormUtils } from "@/pages/demo/demo.utils";
import { useLocation } from "react-router";
import { useDialogFrameDimensions } from "../hooks/useDialogFrameDimensions";
import { useCalendlyAnalytics } from "../hooks/useCalendlyAnalytics";
import { CalendlyError } from "../calendlyError";
import { CALENDLY_URL_PARAM, THANKS_CONFIRMED_URL } from "../constants/calendly";

// Allow-list of hosts the embedded iframe (and same-tab fallback redirect) may
// resolve to. Without this guard, an attacker can craft
// `?calendly_url=https://evil.com` and the InlineWidget renders attacker-
// controlled content inside the form, plus the iframe-blocked fallback
// would issue a top-level redirect to that host (open redirect).
const ALLOWED_CALENDLY_HOSTS = new Set(["calendly.com", "www.calendly.com"]);

const getCalendlyURLFromParams = (): string | null => {
  const raw = new URLSearchParams(window.location.search).get(CALENDLY_URL_PARAM);
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== "https:") return null;
    if (!ALLOWED_CALENDLY_HOSTS.has(parsed.hostname.toLowerCase())) return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

export const ScheduleDemoStep = () => {
  const { dialogFrameHeight } = useDialogFrameDimensions();
  const location = useLocation();
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [retryKey, setRetryKey] = useState(0);
  const [retryCount, setRetryCount] = useState(0);

  const calendlyURL = getCalendlyURLFromParams();

  // Tracks whether we've already fired calendly_iframe_injected (or
  // calendly_container_not_found) for the current attempt. Reset on retry.
  // The previous implementation used `[retryKey]` as the only dep, which
  // meant the effect ran on first mount — when window.location.search may
  // not yet have been updated by history.push — and never re-ran when the
  // URL became available, mis-firing `calendly_container_not_found` for
  // valid Calendly loads.
  const hasTrackedInjectionRef = useRef(false);
  useEffect(() => {
    hasTrackedInjectionRef.current = false;
  }, [retryKey]);

  useEffect(() => {
    if (calendlyURL && !hasTrackedInjectionRef.current) {
      demoFormUtils.track("calendly_iframe_injected", { url: calendlyURL, retry_count: retryCount });
      hasTrackedInjectionRef.current = true;
    }
  }, [calendlyURL, retryCount]);

  // calendly_container_not_found fires only after a settle window, so a
  // momentary null on first render (URL still propagating from history.push)
  // doesn't trigger a false alarm.
  useEffect(() => {
    if (calendlyURL || hasTrackedInjectionRef.current) return;
    const timeout = setTimeout(() => {
      if (!hasTrackedInjectionRef.current) {
        demoFormUtils.track("calendly_container_not_found");
        hasTrackedInjectionRef.current = true;
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [calendlyURL]);

  const { startLoading } = useCalendlyAnalytics({
    onLoadingError: () => {
      setShowError(true);
      setIsLoading(false);
    },
    onLoadingComplete: () => {
      setIsLoading(false);
    },
    onEventScheduled: () => {
      document.location.href = THANKS_CONFIRMED_URL + location.search;
    },
    onIframeBlocked: () => {
      if (calendlyURL) {
        window.location.href = calendlyURL;
      }
    }
  });

  useEffect(() => {
    startLoading();
  }, [startLoading]);

  useEffect(() => {
    analyticsService.page();
  }, []);

  const handleRetry = () => {
    const nextCount = retryCount + 1;
    demoFormUtils.track("calendly_retry_clicked", { retry_count: nextCount });
    setRetryCount(nextCount);
    setShowError(false);
    setRetryKey((prev) => prev + 1);
    startLoading();
    setIsLoading(true);
  };

  return (
    <div className="relative mt-5 flex w-full flex-col" style={{ minHeight: dialogFrameHeight - 100 }}>
      {showError && <CalendlyError onRetry={handleRetry} />}
      {calendlyURL && !showError && (
        <div className="mt-0 md:-mt-16">
          <InlineWidget
            key={retryKey}
            url={calendlyURL}
            styles={{
              width: "100%",
              margin: "0px",
              padding: "0px",
              height: dialogFrameHeight - 100,
              opacity: isLoading ? 0 : 1,
              transition: "opacity 0.3s ease-in-out"
            }}
          />
        </div>
      )}
      <div
        aria-hidden={!isLoading}
        className="pointer-events-none absolute inset-0"
        style={{
          opacity: isLoading ? 1 : 0,
          transition: "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)"
        }}
      >
        <ScheduleLoader showLoader={isLoading} />
      </div>
    </div>
  );
};
